import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  ArrowLeft,
  Landmark,
  ShieldCheck,
  BadgePercent,
  Handshake,
  FileText,
  MapPinned,
  CheckCircle2,
  Search,
  Sparkles,
} from 'lucide-react';
import TopStatsNav from '../components/TopStatsNav';
import { ROUTES } from '../lib/routes';

const MODELS = [
  {
    key: 'PPP',
    title: 'PPP (مشارکت عمومی-خصوصی)',
    badge: 'مدل پایه',
    summary: 'تقسیم ریسک/بازده بین دولت و بخش خصوصی با چارچوب قراردادی شفاف.',
    bestFor: ['پایانه‌های لجستیکی', 'انبارداری و سردخانه', 'خدمات ارزش افزوده'],
    docs: ['مطالعات توجیهی', 'صورت وضعیت دارایی/زمین', 'ضمانت‌ها و تضامین'],
  },
  {
    key: 'BOT',
    title: 'BOT (ساخت-بهره‌برداری-واگذاری)',
    badge: 'زیرساختی',
    summary: 'سرمایه‌گذار پروژه را احداث می‌کند، بهره‌برداری می‌کند و در پایان واگذار می‌نماید.',
    bestFor: ['خطوط ریلی فرعی', 'تجهیزات بندری', 'زیرساخت مرزی'],
    docs: ['طرح فنی', 'مدل درآمدی', 'برنامه زمان‌بندی ساخت'],
  },
  {
    key: 'BOO',
    title: 'BOO (ساخت-مالکیت-بهره‌برداری)',
    badge: 'مالکیت خصوصی',
    summary: 'مالکیت و بهره‌برداری در اختیار سرمایه‌گذار باقی می‌ماند (با مجوزهای لازم).',
    bestFor: ['ناوگان و تجهیزات', 'مراکز توزیع', 'فناوری‌های ردیابی'],
    docs: ['مجوز فعالیت', 'چارچوب تعرفه/خدمات', 'SLA و KPI'],
  },
];

const PERMIT_PATHS = [
  {
    title: 'مسیر اخذ مجوزهای عملیاتی (سرویس/اپراتور)',
    steps: [
      { t: 'ثبت درخواست', d: 'نوع خدمت، دامنه جغرافیایی، ظرفیت و استانداردها را مشخص کنید.' },
      { t: 'ارزیابی اولیه', d: 'بررسی صلاحیت، سوابق، توان مالی و فنی.' },
      { t: 'تکمیل مستندات', d: 'بارگذاری مدارک هویتی/حقوقی، بیمه، استانداردها و تعهدات.' },
      { t: 'بازدید/کنترل میدانی', d: 'کنترل تجهیزات، انبار/پایانه، فرآیند ایمنی و کیفیت.' },
      { t: 'صدور مجوز و SLA', d: 'توافق سطح خدمت، KPI و نحوه گزارش‌دهی.' },
    ],
    hint: 'پیشنهادی: از چک‌لیست اسناد استفاده کنید تا برگشت پرونده کاهش یابد.',
  },
  {
    title: 'مسیر مجوزهای سرمایه‌گذاری/پروژه (زیرساخت)',
    steps: [
      { t: 'انتخاب پروژه/مکان', d: 'حوزه پروژه، زمین/پهنه، و ارتباط با کریدور مشخص شود.' },
      { t: 'مطالعات امکان‌سنجی', d: 'حجم بار، درآمد، ریسک‌ها، و سناریوهای اجرایی.' },
      { t: 'اخذ موافقت اصولی', d: 'تطبیق با طرح‌های بالادستی و الزامات دستگاه‌های مرتبط.' },
      { t: 'انعقاد قرارداد مشارکت', d: 'تعیین مدل (PPP/BOT/BOO)، تضامین، و زمان‌بندی.' },
      { t: 'مجوزهای ساخت و بهره‌برداری', d: 'مجوزهای اجرایی/ایمنی/محیط زیست و سپس بهره‌برداری.' },
    ],
    hint: 'پیشنهادی: ماتریس ریسک/مسئولیت را قبل از قرارداد نهایی کنید.',
  },
];

export default function PermitsAndPartnershipModels() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [activeModel, setActiveModel] = useState('PPP');

  const filteredModels = useMemo(() => {
    const q = String(query || '').trim();
    if (!q) return MODELS;
    return MODELS.filter((m) => (m.title + m.summary + m.key).includes(q));
  }, [query]);

  const model = useMemo(
    () => filteredModels.find((m) => m.key === activeModel) || filteredModels[0] || MODELS[0],
    [filteredModels, activeModel]
  );

  return (
    <div className="min-h-screen bg-[#f8fafc] font-[IRANSansWeb,Tahoma] p-6 md:p-10" dir="rtl">
      <div className="max-w-6xl mx-auto space-y-8">
        <header className="bg-white rounded-[2.5rem] border border-gray-100 shadow-sm p-8 md:p-10 overflow-hidden relative">
          <div className="absolute inset-0 opacity-40 bg-[radial-gradient(circle_at_top,rgba(250,204,21,0.12),transparent_70%)]" />
          <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div className="text-right">
              <div className="inline-flex items-center gap-2 bg-amber-50 text-amber-700 px-4 py-2 rounded-2xl text-[11px] font-extrabold border border-amber-100">
                <Landmark size={16} />
                <span>مسیرهای اخذ مجوز / مدل‌های مشارکت</span>
              </div>

              <h1 className="mt-6 text-3xl md:text-4xl font-black text-gray-900 leading-tight">
                راهنمای تصمیم‌گیری برای مجوز و مشارکت
              </h1>

              <p className="mt-4 text-sm text-gray-500 leading-8">
                این صفحه به‌صورت عملیاتی طراحی شده: مسیرهای اخذ مجوز را مرحله به مرحله می‌بینید، مدل‌های مشارکت را مقایسه می‌کنید،
                و یک بسته پیشنهادی از مدارک/چک‌لیست‌ها برای کاهش برگشت پرونده دریافت می‌کنید.
              </p>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={() => navigate('/dashboard')}
                  className="h-12 px-5 rounded-2xl border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 font-bold text-sm transition inline-flex items-center justify-center gap-2"
                >
                  <ArrowRight size={18} />
                  بازگشت به داشبورد
                </button>

                <button
                  type="button"
                  onClick={() => navigate(ROUTES.systems.tracking)}
                  className="h-12 px-5 rounded-2xl bg-[#a16207] hover:bg-[#854d0e] text-white font-bold text-sm transition inline-flex items-center justify-center gap-2 shadow-lg shadow-amber-900/15"
                >
                  رفتن به رهگیری چندوجهی
                  <ArrowLeft size={18} />
                </button>
              </div>
            </div>

            <div className="bg-[#fbfdff] border border-gray-100 rounded-[2.5rem] p-6 shadow-sm">
              <div className="flex items-start justify-between gap-4">
                <div className="text-right">
                  <div className="text-[11px] text-gray-400 font-bold">پیشنهاد هوشمند</div>
                  <div className="mt-2 text-lg font-black text-gray-800">کاهش ریسک و زمان اخذ مجوز</div>
                  <div className="mt-1 text-[11px] text-gray-500 leading-6">
                    با انتخاب مدل مشارکت مناسب و آماده‌سازی مدارک، احتمال برگشت پرونده و توقف‌های اجرایی کاهش می‌یابد.
                  </div>
                </div>
                <div className="w-12 h-12 rounded-2xl bg-amber-50 border border-amber-100 flex items-center justify-center text-amber-700">
                  <Sparkles size={22} />
                </div>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-3">
                <MiniKpi label="مسیرهای مجوز" value="۲ مسیر اصلی" icon={<MapPinned size={16} />} />
                <MiniKpi label="مدل‌های مشارکت" value="۳ مدل رایج" icon={<Handshake size={16} />} />
                <MiniKpi label="چک‌لیست مدارک" value="پیشنهادی" icon={<FileText size={16} />} />
                <MiniKpi label="KPI/SLA" value="قابل تنظیم" icon={<ShieldCheck size={16} />} />
              </div>
            </div>
          </div>
        </header>

        <TopStatsNav
          items={[
            {
              label: 'خدمات مستقیم',
              value: 4,
              unit: 'مد فعال',
              route: ROUTES.services.maritime,
              accent: 'emerald',
              sparkValues: [2, 4, 4, 6, 7, 8],
            },
            {
              label: 'رهگیری چندوجهی',
              value: 3,
              unit: 'پرونده نمونه',
              route: ROUTES.systems.tracking,
              accent: 'blue',
              sparkValues: [3, 4, 6, 6, 7, 9],
            },
          ]}
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <section className="lg:col-span-2 bg-white rounded-[2.5rem] border border-gray-100 shadow-sm p-6 md:p-8">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="text-right">
                <h2 className="text-lg font-black text-gray-800">مسیرهای اخذ مجوز</h2>
                <p className="text-[11px] text-gray-400 mt-2 leading-6">
                  دو مسیر پیشنهادی (عملیاتی/پروژه) با تمرکز روی کاهش برگشت پرونده و شفاف‌سازی نقش‌ها.
                </p>
              </div>
            </div>

            <div className="mt-6 space-y-6">
              {PERMIT_PATHS.map((p) => (
                <div key={p.title} className="rounded-2xl border border-gray-100 bg-[#fbfdff] p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="text-right">
                      <div className="text-sm font-black text-gray-800">{p.title}</div>
                      <div className="text-[11px] text-gray-400 mt-2 leading-6">{p.hint}</div>
                    </div>
                    <div className="w-10 h-10 rounded-2xl bg-amber-50 border border-amber-100 flex items-center justify-center text-amber-700">
                      <MapPinned size={18} />
                    </div>
                  </div>

                  <div className="mt-5 space-y-3">
                    {p.steps.map((s, idx) => (
                      <div key={s.t} className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-2xl bg-white border border-gray-100 flex items-center justify-center text-gray-700 font-black text-xs flex-shrink-0">
                          {(idx + 1).toLocaleString('fa-IR')}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-[12px] font-extrabold text-gray-800">{s.t}</div>
                          <div className="text-[11px] text-gray-500 mt-1 leading-7">{s.d}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <aside className="bg-white rounded-[2.5rem] border border-gray-100 shadow-sm p-6 md:p-8">
            <div className="flex items-start justify-between gap-6">
              <div className="text-right">
                <h3 className="text-lg font-black text-gray-800">مدل‌های مشارکت</h3>
                <p className="text-[11px] text-gray-400 mt-2 leading-6">جستجو، مقایسه و انتخاب مدل مناسب.</p>
              </div>
              <div className="w-12 h-12 rounded-2xl bg-amber-50 border border-amber-100 flex items-center justify-center text-amber-700">
                <Handshake size={22} />
              </div>
            </div>

            <div className="mt-6 relative">
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="جستجو در مدل‌ها (PPP/BOT/BOO)..."
                className="w-full h-12 pr-12 pl-4 rounded-2xl border border-gray-200 bg-white focus:outline-none focus:border-amber-400 text-sm"
              />
              <Search className="absolute right-4 top-3 text-gray-400" size={20} />
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              {filteredModels.map((m) => (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => setActiveModel(m.key)}
                  className={`px-3 py-2 rounded-2xl border text-[11px] font-extrabold transition ${
                    model.key === m.key ? 'bg-amber-50 text-amber-700 border-amber-100 ring-2 ring-amber-200' : 'bg-white text-gray-600 border-gray-100 hover:bg-gray-50'
                  }`}
                >
                  {m.key}
                </button>
              ))}
            </div>

            <div className="mt-6 rounded-2xl border border-gray-100 bg-[#fbfdff] p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="text-right">
                  <div className="text-[10px] font-extrabold bg-white border border-gray-100 text-gray-600 inline-flex px-3 py-1.5 rounded-full">
                    {model.badge}
                  </div>
                  <div className="mt-3 text-sm font-black text-gray-800">{model.title}</div>
                  <div className="mt-2 text-[11px] text-gray-500 leading-7">{model.summary}</div>
                </div>
                <div className="w-10 h-10 rounded-2xl bg-amber-50 border border-amber-100 flex items-center justify-center text-amber-700">
                  <Landmark size={18} />
                </div>
              </div>

              <div className="mt-5 space-y-4">
                <BulletBlock
                  title="مناسب برای"
                  icon={<BadgePercent size={14} />}
                  items={model.bestFor}
                />
                <BulletBlock
                  title="مدارک پیشنهادی"
                  icon={<FileText size={14} />}
                  items={model.docs}
                />
              </div>
            </div>

            <div className="mt-6 rounded-2xl border border-gray-100 bg-white p-5">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-700 flex-shrink-0">
                  <CheckCircle2 size={18} />
                </div>
                <div className="text-right">
                  <div className="text-sm font-black text-gray-800">اقدام بعدی (پیشنهادی)</div>
                  <div className="text-[11px] text-gray-500 mt-2 leading-7">
                    یک «بسته مدارک اولیه» را آماده کنید و برای ارزیابی سریع، درخواست جلسه/مشاوره ثبت نمایید.
                  </div>
                </div>
              </div>

              <div className="mt-4 flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={() => navigate('/investment')}
                  className="h-12 px-5 rounded-2xl bg-yellow-400 hover:bg-yellow-500 text-[#1A3062] font-black transition inline-flex items-center justify-center gap-2 shadow-lg"
                >
                  مشاهده سرمایه‌گذاری
                  <ArrowLeft size={18} />
                </button>
                <button
                  type="button"
                  onClick={() => navigate(ROUTES.services.maritime)}
                  className="h-12 px-5 rounded-2xl border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 font-bold text-sm transition inline-flex items-center justify-center gap-2"
                >
                  رفتن به خدمات مستقیم
                  <ArrowLeft size={18} />
                </button>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

function MiniKpi({ label, value, icon }) {
  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-4 flex items-start justify-between gap-4">
      <div className="text-right min-w-0">
        <div className="text-[10px] text-gray-400 font-bold">{label}</div>
        <div className="text-[12px] font-extrabold text-gray-800 mt-1 leading-6">{value}</div>
      </div>
      <div className="w-10 h-10 rounded-2xl bg-amber-50 border border-amber-100 flex items-center justify-center text-amber-700 flex-shrink-0">
        {icon}
      </div>
    </div>
  );
}

function BulletBlock({ title, icon, items }) {
  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="text-[12px] font-extrabold text-gray-800">{title}</div>
        <div className="w-9 h-9 rounded-2xl bg-amber-50 border border-amber-100 flex items-center justify-center text-amber-700">
          {icon}
        </div>
      </div>
      <ul className="mt-3 space-y-2 text-[11px] text-gray-600 leading-6">
        {(items || []).map((x) => (
          <li key={x} className="flex items-start gap-2">
            <span className="w-2 h-2 rounded-full bg-amber-400 mt-2 flex-shrink-0" />
            <span className="font-bold">{x}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

